import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ICandidateNote, ISaveNoteCommand } from "../Typings/CandidateProfile";
import { apiPathCandidateProfile, fetcher, secureFetcher } from "./queryHelpers";
import { useAuthToken } from "../State/Hooks/authHooks";

/**
 * Saves a note for a given candidate
 *
 * Called using [variable].mutate(arg: INoteBody)
 *
 * @returns UseMutationResult<boolean, unknown, INoteBody, unknown>
 */
export const useSaveNoteMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, ISaveNoteCommand, ICandidateNote[] | undefined>(
    (saveNoteCommand) =>
      fetcher(apiPathCandidateProfile + "note/save", {
        method: "POST",
        body: JSON.stringify(saveNoteCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onMutate: (saveNoteCommand) => optimisticNoteUpdate(saveNoteCommand, queryClient),
      onSettled: (response, error: any, saveNoteCommand, originalCandidateProfile) => {
        const { candidateId } = saveNoteCommand;
        if ((error || response === false) && originalCandidateProfile) {
          queryClient.setQueryData(["candidateNotes", candidateId], originalCandidateProfile);
        } else {
          return queryClient.invalidateQueries(["candidateNotes", candidateId]);
        }
      },
    }
  );
};

/**
 * Optimiistic update of the candidate profile page when a note is saved
 *
 * @param saveNoteCommand The note body for the note to be saved
 * @param queryClient The react-query client to use for invalidating queries
 * @returns A context object to use in the onSettled callback of the mutation
 */
const optimisticNoteUpdate = async (saveNoteCommand: ISaveNoteCommand, queryClient: QueryClient) => {
  const { candidateId, ...noteDetails } = saveNoteCommand;
  const updateDate = new Date();

  await queryClient.cancelQueries(["candidateNotes", candidateId]);

  const originalCandidateProfile = queryClient.getQueryData<ICandidateNote[]>([
    "candidateNotes",
    candidateId,
  ]);

  if (originalCandidateProfile) {
    queryClient.setQueryData<ICandidateNote[]>(
      ["candidateNotes", candidateId],
      noteDetails.noteId
        ? originalCandidateProfile.map((note: ICandidateNote) =>
            note.id === noteDetails.noteId ? { ...note, ...noteDetails } : note
          )
        : [
            ...originalCandidateProfile,
            {
              ...noteDetails,
              createDate: updateDate,
              id: "",
              lastUpdateDate: updateDate,
              username: "You",
              userNickname: "You",
            },
          ]
    );
  }

  return originalCandidateProfile;
};

export function useCandidateNotesQuery(candidateId: string) {
  const token = useAuthToken();
  return useQuery<ICandidateNote[]>(
    ["candidateNotes", candidateId],
    () => secureFetcher(apiPathCandidateProfile + candidateId + "/note", token),
    {
      enabled: !!candidateId && !!token,
    }
  );
}
